import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Divider } from '@bynder/design-system';
import { checkMaskConflict } from 'packages/pages/editor/RightSideMenu/Shots/Masking/utils';
import { useShortcutsBlocker } from 'packages/hooks/shortcuts/useShortcutsBlocker';
import LayerHeader from 'packages/common/editor/layers/layerHeader/LayerHeader';
import LayerItem from 'packages/common/editor/layers/layerItem/LayerItem';
import { collectTopLevelGroups } from 'packages/common/editor/timeline/timeline-actions/components/utils';
import { getDesUpdateForChildRemoval } from 'packages/common/editor/timeline/timeline-actions/components/desUtils';
import useEditor from 'packages/hooks/useEditor';
import { useArrangeShortcuts } from 'packages/hooks/shortcuts/useArrangeShortcuts';
import { useToggleExpand } from 'packages/common/editor/helpers/groups';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import { useShiftSelection } from 'packages/common/editor/helpers/useShiftSelection';
import { isCtrlKey, isShiftKey } from 'packages/helpers/hotKeys';
import { Draggable } from 'packages/common/editor/layers/layerItem/Draggable';
import { Droppable } from 'packages/common/editor/layers/layerItem/Droppable';
import LayersEmpty from './LayersEmpty';
import { LayersContainer } from './LayerTree.styled';

const getNestedLevel = (element: any, level = 0) =>
    element.parent ? getNestedLevel(element.parent, level + 1) : level;
const isParent = (element: any, id: any) =>
    element.parent ? element.parent.id === id || isParent(element.parent, id) : false;

const checkIsElementAllParentsExpanded = (element: any, expandedGroupIds: any) => {
    if (element.parent) {
        if (expandedGroupIds.includes(element.parent.id)) {
            return checkIsElementAllParentsExpanded(element.parent, expandedGroupIds);
        }

        return false;
    }

    return true;
};

const allowAddPlaceholder = (
    element: any,
    nextElement: any,
    draggingId: any,
    elementLevel: any,
    nextElementLevel: any,
) => {
    const nextId = nextElement?.id;

    if (element.id === draggingId) {
        return false;
    }

    if (isParent(element, draggingId)) {
        return false;
    }

    if (nextElementLevel > elementLevel && nextId === draggingId) {
        return false;
    }

    if (elementLevel === nextElementLevel && nextId === draggingId) {
        return false;
    }

    if (checkMaskConflict(element, draggingId)) {
        return false;
    }

    return true;
};

const dropAnimation = {
    duration: 250,
    easing: 'ease',
};

const LayerTree = () => {
    const { creativeModel, manipulationRenderer } = useEditor();
    const forceUpdate = useForceUpdate();
    const data = useRef<any>({ elements: [], dropTimer: null, draggingElementId: null });
    const { elements, draggingElementId } = data.current;
    const [expandedGroupIds, toggleGroupExpand] = useToggleExpand([]);
    const draggingElement = useMemo(
        () => elements.find((el: any) => el.id === draggingElementId),
        [elements, draggingElementId],
    );
    const currentPageIndex = creativeModel.getCurrentPageIndex();
    const selectedElements = manipulationRenderer?.getSelectedElements();

    const [selectedGroups, setSelectedGroups] = useState(() =>
        collectTopLevelGroups(manipulationRenderer?.getSelectedElements() || []),
    );

    const { handleShiftClick, setAnchor } = useShiftSelection(manipulationRenderer);

    const handleItemClick = useCallback(
        (elementId: any, event: any) => {
            if (isShiftKey(event)) {
                // Use full flattened tree (includes children inside collapsed groups)
                const flatIds = elements.map((el: any) => el.id);
                handleShiftClick(elementId, flatIds);
            } else {
                const isMultiSelectMode = isCtrlKey(event);
                manipulationRenderer.selectElement(elementId, isMultiSelectMode);

                if (!isMultiSelectMode) {
                    setAnchor(elementId);
                }
            }
        },
        [elements, handleShiftClick, setAnchor, manipulationRenderer],
    );

    const handleDragCancel = useCallback(() => {
        data.current.draggingElementId = null;
        forceUpdate();
    }, []);

    const { toggleShortcutsBlocker } = useShortcutsBlocker(handleDragCancel);

    const filterCollapsedElement = useCallback(
        (element: any) => checkIsElementAllParentsExpanded(element, expandedGroupIds),
        [expandedGroupIds],
    );

    const elementsUpdateListener = useCallback(() => {
        data.current.elements = creativeModel.getAllElementsRecursively();

        forceUpdate();
    }, [creativeModel, forceUpdate]);

    const handleDragStart = useCallback(
        (event: any) => {
            toggleShortcutsBlocker(true);
            data.current.draggingElementId = event.active.id;
            forceUpdate();
        },
        [toggleShortcutsBlocker, forceUpdate],
    );

    const handleDragEnd = useCallback(
        ({ over }: any) => {
            toggleShortcutsBlocker(false);

            const { draggingElementId: elementId, elements } = data.current;
            const element = elements.find((el: any) => el.id === elementId);

            if (over && elementId) {
                const { type, id, parentId, renderOrder } = over.data.current;

                if (type === 'group') {
                    const desUpdate = getDesUpdateForChildRemoval(element);

                    creativeModel.beginAccumulation();
                    creativeModel.updateElement(elementId, { renderOrder });
                    creativeModel.addExistingElementToGroup(elementId, id);

                    if (desUpdate) {
                        creativeModel.updateElement(desUpdate.desGroupId, {
                            activeDropdownElementId: desUpdate.newActiveChildId,
                        });
                    }

                    creativeModel.endAccumulation();
                    manipulationRenderer.selectElement(elementId);
                } else if (type === 'element') {
                    const desParent = element.parent;
                    const isDesGroup = desParent?.onlyAllowOneSelection;
                    const activeChildBeingGrouped =
                        isDesGroup &&
                        (desParent.activeDropdownElementId === elementId || desParent.activeDropdownElementId === id);

                    creativeModel.beginAccumulation();
                    creativeModel.updateElement(elementId, { renderOrder });
                    creativeModel.updateElement(id, { renderOrder: renderOrder - 1 });
                    const newGroupEl = creativeModel.wrapElementsIntoGroup([id, elementId]);

                    if (activeChildBeingGrouped) {
                        creativeModel.updateElement(desParent.id, {
                            activeDropdownElementId: newGroupEl.id,
                        });
                    }

                    creativeModel.endAccumulation();
                    manipulationRenderer.selectElement(newGroupEl.id);
                } else {
                    const deprecatedGroupSelected = element.children?.length
                        ? element.isContainsElement(parentId)
                        : false;

                    if (!deprecatedGroupSelected && parentId !== elementId) {
                        const isParentChanging = element.parent && element.parent.id !== parentId;
                        const desUpdate = isParentChanging ? getDesUpdateForChildRemoval(element) : null;

                        creativeModel.beginAccumulation();

                        if (isParentChanging) {
                            creativeModel.unwrapElementFromGroup(elementId);
                        }

                        creativeModel.updateElement(elementId, { renderOrder });

                        if (parentId && (!element.parent || element.parent.id !== parentId)) {
                            creativeModel.addExistingElementToGroup(elementId, parentId);
                        }

                        if (desUpdate) {
                            creativeModel.updateElement(desUpdate.desGroupId, {
                                activeDropdownElementId: desUpdate.newActiveChildId,
                            });
                        }

                        creativeModel.endAccumulation();
                        manipulationRenderer.selectElement(elementId);
                    }
                }
            }

            if (data.current.dropTimer) {
                clearTimeout(data.current.dropTimer);
            }
            data.current.dropTimer = setTimeout(() => {
                data.current.draggingElementId = null;
                data.current.dropTimer = null;
                forceUpdate();
            }, dropAnimation.duration);

            forceUpdate();
        },
        [forceUpdate, creativeModel, manipulationRenderer, toggleShortcutsBlocker],
    );

    const mouseSensor = useSensor(MouseSensor, {
        // Press delay with tolerance of 20px of movement
        activationConstraint: {
            distance: 20,
        },
    });

    const sensors = useSensors(mouseSensor);

    useArrangeShortcuts({ manipulationRenderer });

    useEffect(() => {
        // Get all elements on initial load
        data.current.elements = creativeModel.getAllElementsRecursively();
    }, []);

    useEffect(() => {
        if (!creativeModel || !manipulationRenderer) {
            return;
        }

        const listener = () => {
            const newGroups = collectTopLevelGroups(manipulationRenderer.getSelectedElements());
            setSelectedGroups(newGroups);
            forceUpdate();
        };

        creativeModel.on('elementsTreeUpdated', elementsUpdateListener);
        creativeModel.on('currentPageChange', elementsUpdateListener);

        manipulationRenderer.eventEmitter.on('elementSelected', listener);

        return () => {
            creativeModel.off('elementsTreeUpdated', elementsUpdateListener);
            creativeModel.off('currentPageChange', elementsUpdateListener);
            manipulationRenderer.eventEmitter.off('elementSelected', listener);
        };
    }, [creativeModel, elementsUpdateListener, manipulationRenderer]);

    const treeElements = useMemo(() => {
        const list = [];
        const addPrevLevelDraggable = (nextLevel: any, level: any, el: any) => {
            if (nextLevel >= level) {
                return;
            }

            if (checkMaskConflict(el, draggingElementId)) {
                return;
            }

            const { parent } = el;
            const renderOrder = parent.renderOrder - 0.5;

            list.push(
                <Droppable
                    level={level - 1}
                    key={`${el.id}-${renderOrder}`}
                    renderOrder={renderOrder}
                    parentId={parent.parent?.id || null}
                />,
            );
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            parent && addPrevLevelDraggable(nextLevel, level - 1, parent);
        };
        const filteredElements = elements.filter(filterCollapsedElement);
        filteredElements.forEach((element: any, index: any) => {
            if (draggingElementId && !index && draggingElementId !== element.id) {
                const renderOrder = element.renderOrder + 0.5;
                // @ts-expect-error TODO(VBS-25739) Droppable parentId omitted at runtime
                list.push(<Droppable key={renderOrder} renderOrder={renderOrder} />);
            }

            const level = getNestedLevel(element);
            list.push(
                <Draggable
                    key={element.id}
                    level={level}
                    element={element}
                    expandedGroupIds={expandedGroupIds}
                    toggleGroupExpand={toggleGroupExpand}
                    draggingElementId={draggingElementId}
                    onItemClick={handleItemClick}
                />,
            );

            const nextElement = filteredElements[index + 1];
            const nextLevel = nextElement ? getNestedLevel(nextElement) : level;

            if (draggingElementId && allowAddPlaceholder(element, nextElement, draggingElementId, level, nextLevel)) {
                const l = nextLevel > level ? nextLevel : level;
                const el = nextLevel > level ? nextElement : element;
                const renderOrder = element.renderOrder - 0.5;

                list.push(
                    <Droppable
                        level={l}
                        key={renderOrder}
                        renderOrder={renderOrder}
                        parentId={el.parent?.id || null}
                    />,
                );
            }

            if (draggingElementId) {
                addPrevLevelDraggable(nextLevel, level, element);
            }
        });

        if (draggingElementId) {
            list.push(<Droppable key="latest" renderOrder={0.5} parentId={null} fillHeight />);
        }

        return list;
    }, [filterCollapsedElement, toggleGroupExpand, expandedGroupIds, draggingElementId, elements, handleItemClick]);

    return (
        <LayersContainer>
            <LayerHeader selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups} />
            <Divider />
            <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <div className="layers--wrapper">
                    {!elements.length && <LayersEmpty />}
                    {treeElements}
                    <DragOverlay dropAnimation={dropAnimation}>
                        {draggingElementId && draggingElement && (
                            <LayerItem
                                element={draggingElement}
                                childrenLength={(draggingElement.children || []).length}
                                onGroupExpand={toggleGroupExpand}
                                level={getNestedLevel(draggingElement)}
                                // eslint-disable-next-line no-implicit-coercion
                                isGroupExpand={!!~expandedGroupIds.indexOf(draggingElementId)}
                                isDragOverlay
                            />
                        )}
                    </DragOverlay>
                </div>
            </DndContext>
        </LayersContainer>
    );
};

export default LayerTree;
